/**
 * Order Medication Service
 * Comprehensive service for managing medication orders with all scenarios
 */

import apiClient from './apiClient';
import {
    MedicationOrderDTO,
    CreateMedicationOrderRequest,
    MedicationOrderStatus,
    PageResponse,
} from '../types/medication.types';

const BASE_PATH = '/v1/medication-orders';

/**
 * Interface for medication order filters
 */
export interface MedicationOrderFilters {
    residentId?: string;
    status?: MedicationOrderStatus;
    startDate?: string;
    endDate?: string;
    medicationName?: string;
    prescribingPhysician?: string;
    facilityId?: string;
}

/**
 * Interface for bulk operations
 */
export interface BulkOperationResult {
    successCount: number;
    failureCount: number;
    errors: Array<{ id: string; error: string }>;
}

/**
 * Interface for medication order statistics
 */
export interface MedicationOrderStats {
    totalOrders: number;
    activeOrders: number;
    completedOrders: number;
    cancelledOrders: number;
    onHoldOrders: number;
    expiringOrders: number;
}

class OrderMedicationService {
    // ============================================
    // CREATE OPERATIONS
    // ============================================

    /**
     * Create a new medication order with one or more medications
     */
    async createOrder(request: CreateMedicationOrderRequest): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.post<MedicationOrderDTO>(BASE_PATH, request);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to create medication order');
        }
    }

    /**
     * Duplicate an existing medication order
     */
    async duplicateOrder(orderId: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.post<MedicationOrderDTO>(`${BASE_PATH}/${orderId}/duplicate`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to duplicate medication order');
        }
    }

    // ============================================
    // READ OPERATIONS
    // ============================================

    /**
     * Get medication order by ID
     */
    async getOrderById(id: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.get<MedicationOrderDTO>(`${BASE_PATH}/${id}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch medication order');
        }
    }

    /**
     * Get medication order by order number
     */
    async getOrderByOrderNumber(orderNumber: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.get<MedicationOrderDTO>(`${BASE_PATH}/order-number/${orderNumber}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch medication order by order number');
        }
    }

    /**
     * Get all medication orders with pagination
     */
    async getAllOrders(page: number = 0, size: number = 20): Promise<PageResponse<MedicationOrderDTO>> {
        try {
            const response = await apiClient.get<PageResponse<MedicationOrderDTO>>(BASE_PATH, {
                params: { page, size },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch medication orders');
        }
    }

    /**
     * Get medication orders with advanced filtering
     */
    async getFilteredOrders(
        filters: MedicationOrderFilters,
        page: number = 0,
        size: number = 20
    ): Promise<PageResponse<MedicationOrderDTO>> {
        try {
            const response = await apiClient.get<PageResponse<MedicationOrderDTO>>(`${BASE_PATH}/filter`, {
                params: { ...filters, page, size },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch filtered medication orders');
        }
    }

    /**
     * Get all medication orders for a specific resident
     */
    async getOrdersByResident(residentId: string): Promise<MedicationOrderDTO[]> {
        try {
            const response = await apiClient.get<MedicationOrderDTO[]>(`${BASE_PATH}/resident/${residentId}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch resident medication orders');
        }
    }

    /**
     * Get active medication orders for a specific resident
     */
    async getActiveOrdersByResident(residentId: string): Promise<MedicationOrderDTO[]> {
        try {
            const response = await apiClient.get<MedicationOrderDTO[]>(`${BASE_PATH}/resident/${residentId}/active`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch active medication orders');
        }
    }

    /**
     * Get medication orders by status
     */
    async getOrdersByStatus(status: MedicationOrderStatus): Promise<MedicationOrderDTO[]> {
        try {
            const response = await apiClient.get<MedicationOrderDTO[]>(`${BASE_PATH}/status/${status}`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch medication orders by status');
        }
    }

    /**
     * Get medication orders by facility
     */
    async getOrdersByFacility(facilityId: string, page: number = 0, size: number = 20): Promise<PageResponse<MedicationOrderDTO>> {
        try {
            const response = await apiClient.get<PageResponse<MedicationOrderDTO>>(`${BASE_PATH}/facility/${facilityId}`, {
                params: { page, size },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch facility medication orders');
        }
    }

    /**
     * Get expiring medication orders (ending within specified days)
     */
    async getExpiringOrders(days: number = 7): Promise<MedicationOrderDTO[]> {
        try {
            const response = await apiClient.get<MedicationOrderDTO[]>(`${BASE_PATH}/expiring`, {
                params: { days },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch expiring medication orders');
        }
    }

    /**
     * Get medication order statistics
     */
    async getOrderStatistics(facilityId?: string): Promise<MedicationOrderStats> {
        try {
            const response = await apiClient.get<MedicationOrderStats>(`${BASE_PATH}/statistics`, {
                params: facilityId ? { facilityId } : {},
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch medication order statistics');
        }
    }

    /**
     * Search medication orders by medication name
     */
    async searchByMedicationName(medicationName: string): Promise<MedicationOrderDTO[]> {
        try {
            const response = await apiClient.get<MedicationOrderDTO[]>(`${BASE_PATH}/search`, {
                params: { medicationName },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to search medication orders');
        }
    }

    // ============================================
    // UPDATE OPERATIONS
    // ============================================

    /**
     * Update medication order
     */
    async updateOrder(id: string, data: Partial<MedicationOrderDTO>): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.put<MedicationOrderDTO>(`${BASE_PATH}/${id}`, data);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to update medication order');
        }
    }

    /**
     * Update medication order status
     */
    async updateOrderStatus(id: string, status: MedicationOrderStatus): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.patch<MedicationOrderDTO>(`${BASE_PATH}/${id}/status`, null, {
                params: { status },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to update medication order status');
        }
    }

    /**
     * Approve medication order
     */
    async approveOrder(id: string, approverNotes?: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.post<MedicationOrderDTO>(`${BASE_PATH}/${id}/approve`, {
                approverNotes,
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to approve medication order');
        }
    }

    /**
     * Reject medication order
     */
    async rejectOrder(id: string, reason: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.post<MedicationOrderDTO>(`${BASE_PATH}/${id}/reject`, null, {
                params: { reason },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to reject medication order');
        }
    }

    /**
     * Discontinue medication order
     */
    async discontinueOrder(id: string, reason: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.post<MedicationOrderDTO>(`${BASE_PATH}/${id}/discontinue`, null, {
                params: { reason },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to discontinue medication order');
        }
    }

    /**
     * Put medication order on hold
     */
    async holdOrder(id: string, reason: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.post<MedicationOrderDTO>(`${BASE_PATH}/${id}/hold`, null, {
                params: { reason },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to put medication order on hold');
        }
    }

    /**
     * Resume medication order from hold
     */
    async resumeOrder(id: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.post<MedicationOrderDTO>(`${BASE_PATH}/${id}/resume`);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to resume medication order');
        }
    }

    /**
     * Renew medication order
     */
    async renewOrder(id: string, newEndDate?: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.post<MedicationOrderDTO>(`${BASE_PATH}/${id}/renew`, null, {
                params: newEndDate ? { newEndDate } : {},
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to renew medication order');
        }
    }

    /**
     * Modify dosage for medication order
     */
    async modifyDosage(id: string, newDosage: string, reason: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.patch<MedicationOrderDTO>(`${BASE_PATH}/${id}/dosage`, null, {
                params: { newDosage, reason },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to modify medication dosage');
        }
    }

    /**
     * Modify frequency for medication order
     */
    async modifyFrequency(id: string, newFrequency: string, reason: string): Promise<MedicationOrderDTO> {
        try {
            const response = await apiClient.patch<MedicationOrderDTO>(`${BASE_PATH}/${id}/frequency`, null, {
                params: { newFrequency, reason },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to modify medication frequency');
        }
    }

    // ============================================
    // DELETE OPERATIONS
    // ============================================

    /**
     * Delete medication order
     */
    async deleteOrder(id: string): Promise<void> {
        try {
            await apiClient.delete(`${BASE_PATH}/${id}`);
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to delete medication order');
        }
    }

    /**
     * Delete multiple medication orders
     */
    async deleteBulkOrders(ids: string[]): Promise<BulkOperationResult> {
        try {
            const response = await apiClient.post<BulkOperationResult>(`${BASE_PATH}/bulk-delete`, { ids });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to delete bulk medication orders');
        }
    }

    // ============================================
    // REPORTING & EXPORT OPERATIONS
    // ============================================

    /**
     * Export medication orders to CSV
     */
    async exportToCSV(filters?: MedicationOrderFilters): Promise<Blob> {
        try {
            const response = await apiClient.get(`${BASE_PATH}/export/csv`, {
                params: filters,
                responseType: 'blob',
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to export medication orders to CSV');
        }
    }

    /**
     * Export medication orders to PDF
     */
    async exportToPDF(filters?: MedicationOrderFilters): Promise<Blob> {
        try {
            const response = await apiClient.get(`${BASE_PATH}/export/pdf`, {
                params: filters,
                responseType: 'blob',
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to export medication orders to PDF');
        }
    }

    /**
     * Print medication order
     */
    async printOrder(id: string): Promise<Blob> {
        try {
            const response = await apiClient.get(`${BASE_PATH}/${id}/print`, {
                responseType: 'blob',
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to print medication order');
        }
    }

    /**
     * Generate medication administration record (MAR)
     */
    async generateMAR(residentId: string, startDate: string, endDate: string): Promise<Blob> {
        try {
            const response = await apiClient.get(`${BASE_PATH}/mar/${residentId}`, {
                params: { startDate, endDate },
                responseType: 'blob',
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to generate MAR');
        }
    }

    // ============================================
    // VALIDATION & VERIFICATION OPERATIONS
    // ============================================

    /**
     * Validate medication order before creation
     */
    async validateOrder(request: CreateMedicationOrderRequest): Promise<{ valid: boolean; errors: string[] }> {
        try {
            const response = await apiClient.post<{ valid: boolean; errors: string[] }>(`${BASE_PATH}/validate`, request);
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to validate medication order');
        }
    }

    /**
     * Check for drug interactions
     */
    async checkDrugInteractions(residentId: string, medicationName: string): Promise<{ hasInteractions: boolean; interactions: string[] }> {
        try {
            const response = await apiClient.get<{ hasInteractions: boolean; interactions: string[] }>(`${BASE_PATH}/interactions`, {
                params: { residentId, medicationName },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to check drug interactions');
        }
    }

    /**
     * Check for duplicate orders
     */
    async checkDuplicates(residentId: string, medicationName: string): Promise<{ hasDuplicates: boolean; duplicates: MedicationOrderDTO[] }> {
        try {
            const response = await apiClient.get<{ hasDuplicates: boolean; duplicates: MedicationOrderDTO[] }>(`${BASE_PATH}/duplicates`, {
                params: { residentId, medicationName },
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to check for duplicate orders');
        }
    }

    // ============================================
    // NOTIFICATION & ALERT OPERATIONS
    // ============================================

    /**
     * Send notification for medication order
     */
    async sendNotification(id: string, recipientIds: string[], message: string): Promise<void> {
        try {
            await apiClient.post(`${BASE_PATH}/${id}/notify`, {
                recipientIds,
                message,
            });
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to send notification');
        }
    }

    /**
     * Get alerts for medication orders (expiring, missing, etc.)
     */
    async getAlerts(facilityId?: string): Promise<Array<{ type: string; message: string; orderId: string }>> {
        try {
            const response = await apiClient.get<Array<{ type: string; message: string; orderId: string }>>(`${BASE_PATH}/alerts`, {
                params: facilityId ? { facilityId } : {},
            });
            return response.data;
        } catch (error: any) {
            throw new Error(error.response?.data?.message || 'Failed to fetch alerts');
        }
    }
}

export default new OrderMedicationService();
